/* eslint-disable no-unused-vars */
import { EXERCISE_DATABASE, EX } from './exercises.js';

// ─── ÉQUIPEMENT ───────────────────────────────────────────────────────────────

export function getAvailableExercises(location, homeEquip = []) {
  if (location === "salle") return EX;
  return EX.filter(ex => {
    const exerciseData = EXERCISE_DATABASE[ex.name];
    if (!exerciseData) return true;
    if (!exerciseData.alternatives || exerciseData.alternatives.length === 0 ||
        (exerciseData.alternatives.length === 1 && exerciseData.alternatives[0].name === null)) return false;
    for (const alt of exerciseData.alternatives) {
      if (alt.name === null) continue;
      if (alt.needs.every(need => homeEquip.includes(need))) return true;
    }
    return false;
  });
}

export function adaptExercise(exerciseName, availableEquipment = []) {
  const exerciseData = EXERCISE_DATABASE[exerciseName];
  if (!exerciseData) return exerciseName;
  if (!exerciseData.alternatives || exerciseData.alternatives.length === 0 ||
      (exerciseData.alternatives.length === 1 && exerciseData.alternatives[0].name === null)) return null;
  for (const alt of exerciseData.alternatives) {
    if (alt.name === null) continue;
    if (alt.needs.every(need => availableEquipment.includes(need))) return alt.name;
  }
  return null;
}

// ─── GÉNÉRATEUR PRINCIPAL ─────────────────────────────────────────────────────
// Signature : (duration, days, gender, equipment, homeEquip, exerciseWeights)
// duration : 30 | 45 | 60 | 75 | 90 (minutes)
// level supprimé — durée pilote le volume, poids via historique exerciseWeights

export function generateAdaptedPrograms(duration = 60, days, gender, equipment, homeEquip = [], exerciseWeights = {}) {

  // ── Volume par durée ──────────────────────────────────────────────────────
  // 30min→4ex/4s=30min, 45min→6ex/4s=45min, 1h→8ex/4s=60min, 1h15→8ex/5s=78min, 1h30→8ex/5s+allongé
  const nbEx   = duration <= 30 ? 4 : duration <= 45 ? 6 : 8;
  const nbSets = duration <= 60 ? 4 : duration <= 75 ? 5 : 6;

  // ── Équipement disponible ─────────────────────────────────────────────────
  const availableEquipment = equipment === "salle"
    ? ["barre", "halteres", "banc", "barre_traction", "kettlebells", "elastiques", "salle"]
    : (homeEquip || []);

  // ── Filtrer + mapper exercices par muscle vers alternatives faisables ──────
  const getExercisesForMuscle = (muscle) => {
    const filtered = EX.filter(ex => {
      if (ex.muscle !== muscle) return false;
      const exerciseData = EXERCISE_DATABASE[ex.name];
      if (!exerciseData) return true;
      if (!exerciseData.alternatives || exerciseData.alternatives.length === 0 ||
          (exerciseData.alternatives.length === 1 && exerciseData.alternatives[0].name === null)) return false;
      for (const alt of exerciseData.alternatives) {
        if (alt.name === null) continue;
        if (alt.needs.every(need => availableEquipment.includes(need))) return true;
      }
      return false;
    });
    const mapped = filtered.map(ex => {
      const exerciseData = EXERCISE_DATABASE[ex.name];
      if (!exerciseData) return ex;
      for (const alt of exerciseData.alternatives) {
        if (alt.name === null) continue;
        if (alt.needs.every(need => availableEquipment.includes(need))) return { ...ex, name: alt.name };
      }
      return ex;
    });
    const seen = new Set();
    return mapped.filter(ex => seen.has(ex.name) ? false : seen.add(ex.name));
  };

  // ── Pools par groupe musculaire ───────────────────────────────────────────
  const pecs     = getExercisesForMuscle("Pectoraux");
  const dos      = getExercisesForMuscle("Dos");
  const epaules  = getExercisesForMuscle("Épaules");
  const biceps   = getExercisesForMuscle("Biceps");
  const triceps  = getExercisesForMuscle("Triceps");
  const quads    = getExercisesForMuscle("Quadriceps");
  const ischios  = getExercisesForMuscle("Ischio-jambiers");
  const fessiers = getExercisesForMuscle("Fessiers");
  const mollets  = getExercisesForMuscle("Mollets");
  const abdos    = getExercisesForMuscle("Abdominaux");

  const totalExercises = pecs.length + dos.length + quads.length;
  if (totalExercises === 0) return [];

  // ── Moteur de calibration (inchangé — utilise l'historique exerciseWeights) ─
  const BW_RATIO = {
    "Pompes classiques":0.64,"Pompes":0.64,"Pompes larges":0.67,"Pompes inclinées":0.55,
    "Pompes déclinées":0.74,"Dips":0.85,"Tractions pronation":1.00,"Tractions supination":1.00,
    "Tractions neutres":1.00,"Tractions":1.00,"Rowing au poids du corps":0.70,
    "Squat bulgare":0.50,"Fentes avant":0.50,"Fentes arrière":0.50,"Fentes":0.50,
    "Pistol squat":0.85,"Squat sumo":0.75,"Squat gobelet":0.75,"Glute bridge":0.60,
    "Hip thrust PDC":0.60,"Pike push-up":0.60,"Handstand push-up":0.85,
    "Dips banc":0.55,"Extensions triceps sol":0.45,"Chin-up":1.00,"Planche":0.70,
  };
  const MUSCLE_GROUPS = {
    poussee_horiz:   ["Développé couché barre","Développé couché haltères","Pompes larges","Pompes classiques","Pompes","Pompes inclinées","Pompes déclinées"],
    poussee_inclinee:["Développé incliné barre","Développé incliné haltères","Pompes inclinées"],
    poussee_vertic:  ["Développé militaire barre","Développé militaire haltères","Pike push-up","Handstand push-up"],
    tirage_vertic:   ["Tractions pronation","Tractions supination","Tractions neutres","Tractions","Chin-up","Tirage vertical poulie","Tirage vertical haltères"],
    tirage_horiz:    ["Rowing barre","Rowing haltères","Rowing câble","Rowing machine","Rowing au poids du corps"],
    quad_compound:   ["Squat barre","Gobelet squat","Squat haltères","Fentes avant","Fentes arrière","Fentes","Squat bulgare"],
    ischio_fessier:  ["Soulevé de terre","Hip thrust barre","Hip thrust haltères","Glute bridge","Hip thrust PDC","Leg curl"],
    triceps:         ["Dips","Dips banc","Barre au front","Extensions triceps poulie","Extensions triceps sol"],
    biceps:          ["Curl barre","Curl haltères","Curl marteau","Chin-up","Tractions supination"],
  };
  const BW_REF = gender === "femme" ? 60 : 75;
  const epley1RM = (reps, weight) => weight > 0 ? weight * (1 + reps / 30) : 0;
  const repsAt = (oneRM, pct) => { if (oneRM <= 0) return null; const w = oneRM * pct; return Math.round(30 * (oneRM / w - 1)); };
  const findStrengthSignal = (muscleGroup) => {
    const names = MUSCLE_GROUPS[muscleGroup] || [];
    for (const name of names) { if (exerciseWeights[name] && exerciseWeights[name] > 0) return { exName: name, weight: exerciseWeights[name] }; }
    return null;
  };
  const isPDC = (exName) => exName in BW_RATIO;
  const calibrateSets = (exName, baseReps, baseWeight, muscleGroup) => {
    if (!muscleGroup || !exerciseWeights || Object.keys(exerciseWeights).length === 0) return { reps: baseReps, weight: baseWeight };
    const signal = findStrengthSignal(muscleGroup);
    if (!signal) return { reps: baseReps, weight: baseWeight };
    const { exName: refName, weight: refWeight } = signal;
    const refIsPDC = isPDC(refName); const targetIsPDC = isPDC(exName);
    if (!refIsPDC && targetIsPDC) {
      const bwRatio = BW_RATIO[exName] || 0.65; const effectiveBW = BW_REF * bwRatio;
      const signal1RM = epley1RM(baseReps, refWeight);
      const repsCalibrated = repsAt(signal1RM, effectiveBW / signal1RM);
      if (repsCalibrated && repsCalibrated > 0 && repsCalibrated <= 60) return { reps: Math.max(5, Math.min(50, repsCalibrated)), weight: 0 };
      return { reps: baseReps, weight: 0 };
    }
    if (refIsPDC && !targetIsPDC) {
      const nonPDCsignal = Object.entries(exerciseWeights).find(([n, w]) => (MUSCLE_GROUPS[muscleGroup] || []).includes(n) && !isPDC(n) && w > 0);
      if (nonPDCsignal) { const [, w] = nonPDCsignal; const oneRM = epley1RM(baseReps, w); const workWeight = Math.round((oneRM * 0.70) / 2.5) * 2.5; return { reps: baseReps, weight: Math.max(0, workWeight) }; }
      return { reps: baseReps, weight: baseWeight };
    }
    if (!refIsPDC && !targetIsPDC && refWeight > 0) {
      const oneRM = epley1RM(baseReps, refWeight); const workWeight = Math.round((oneRM * 0.72) / 2.5) * 2.5;
      const clamped = Math.min(refWeight * 1.3, Math.max(refWeight * 0.4, workWeight));
      return { reps: baseReps, weight: Math.max(0, Math.round(clamped / 2.5) * 2.5) };
    }
    return { reps: baseReps, weight: 0 };
  };
  const EX_TO_MUSCLE_GROUP = {
    "Développé couché barre":"poussee_horiz","Développé couché haltères":"poussee_horiz",
    "Pompes larges":"poussee_horiz","Pompes classiques":"poussee_horiz","Pompes":"poussee_horiz",
    "Pompes déclinées":"poussee_horiz","Développé poitrine élastique":"poussee_horiz",
    "Développé incliné barre":"poussee_inclinee","Développé incliné haltères":"poussee_inclinee","Pompes inclinées":"poussee_inclinee",
    "Écarté haltères plat":"poussee_horiz","Écarté poulie":"poussee_horiz",
    "Développé militaire barre":"poussee_vertic","Développé militaire haltères":"poussee_vertic","Pike push-up":"poussee_vertic","Handstand push-up":"poussee_vertic",
    "Élévations latérales":null,"Face pull":null,
    "Tractions pronation":"tirage_vertic","Tractions supination":"tirage_vertic","Tractions neutres":"tirage_vertic","Tractions":"tirage_vertic","Chin-up":"tirage_vertic",
    "Tirage vertical poulie":"tirage_vertic","Tirage vertical haltères":"tirage_vertic",
    "Rowing barre":"tirage_horiz","Rowing haltères":"tirage_horiz","Rowing câble":"tirage_horiz","Rowing machine":"tirage_horiz","Rowing au poids du corps":"tirage_horiz",
    "Soulevé de terre":"ischio_fessier",
    "Squat barre":"quad_compound","Gobelet squat":"quad_compound","Squat haltères":"quad_compound",
    "Fentes avant":"quad_compound","Fentes arrière":"quad_compound","Fentes":"quad_compound","Squat bulgare":"quad_compound","Leg press":"quad_compound",
    "Extension quadriceps":null,"Leg curl":"ischio_fessier",
    "Hip thrust barre":"ischio_fessier","Hip thrust haltères":"ischio_fessier","Glute bridge":"ischio_fessier","Hip thrust PDC":"ischio_fessier",
    "Dips":"triceps","Dips banc":"triceps","Barre au front":"triceps","Extensions triceps poulie":"triceps","Extensions triceps sol":"triceps","Triceps poulie":"triceps",
    "Curl barre":"biceps","Curl haltères":"biceps","Curl marteau":"biceps",
  };

  const isBodyweight = (ex) => {
    if (!ex) return false;
    const data = EXERCISE_DATABASE[ex.name];
    if (!data) return true;
    for (const alt of data.alternatives) {
      if (alt.name === null) continue;
      if (alt.needs.every(need => availableEquipment.includes(need))) return alt.needs.length === 0;
    }
    return true;
  };

  // ── mkExCalibrated : construit un exercice avec nbSets séries, weight:0 par défaut ─
  // Si exerciseWeights contient un historique, calibrateSets l'utilise automatiquement
  const mkExCalibrated = (ex, restTime, baseReps) => {
    if (!ex) return null;
    const bw = isBodyweight(ex);
    const muscleGroup = EX_TO_MUSCLE_GROUP[ex.name] || null;
    const finalSets = Array.from({ length: nbSets }, (_, i) => {
      if (bw) {
        const cal = calibrateSets(ex.name, baseReps, 0, muscleGroup);
        const repsDelta = i === nbSets - 1 ? Math.max(0, Math.round(cal.reps * 0.1)) : 0;
        return { reps: Math.max(1, cal.reps - repsDelta), weight: 0 };
      } else {
        const cal = calibrateSets(ex.name, baseReps, 0, muscleGroup);
        return { reps: baseReps, weight: Math.max(0, cal.weight) };
      }
    });
    return { id: ex.id, name: ex.name, muscle: ex.muscle, detail: ex.detail, restTime, plannedSets: finalSets };
  };

  // ── pickSlot : choisit le meilleur exercice dans un pool selon priorités ──
  const pickSlot = (pool, priorityNames, usedIds = new Set()) => {
    const available = pool.filter(ex => !usedIds.has(ex.id));
    if (available.length === 0) return null;
    for (const name of priorityNames) { const found = available.find(ex => ex.name === name); if (found) return found; }
    return available[0];
  };

  // ── Groupes de variantes fonctionnellement identiques (un seul par séance) ──
  const VARIANT_GROUPS = [
    // Leg curl : toutes les machines/positions travaillent le même mouvement
    ["Leg curl couché machine","Leg curl allongé","Leg curl assis machine","Leg curl assis","Nordic curl GHR","Glute ham raise"],
    // Hip thrust : toutes les variantes = même pattern (bilatéral et unilatéral)
    ["Hip thrust machine","Hip thrust barre","Hip Thrust avec bande","Hip thrust au sol","Pont fessier au sol","Hip thrust haltères","Hip thrust unilatéral","Glute bridge unilatéral","Pont fessier","Hip thrust"],
    // Fentes : même pattern unilateral quad-dominant
    ["Fentes avant haltères","Fentes avant","Fentes arrière reverse lunge","Fentes alternées","Fentes marchées haltères","Fentes marchées kettlebell","Fentes sautées","Fentes latérales"],
    // Curl barre / EZ / haltères simultané — même mouvement biceps bilatéral
    ["Curl barre droite","Curl barre EZ","Curl haltères simultané"],
    // Curl alterné / concentration — même mouvement biceps unilatéral
    ["Curl haltères alterné","Curl concentration","Curl marteau"],
    // Extensions triceps nuque
    ["Extension nuque haltère","Extension haltère allongé"],
    // Tractions : toutes les prises = même mouvement vertical tirage
    ["Tractions pronation large","Tractions supination serrée","Tractions neutres","Rowing inversé"],
    // Rowing horizontal : toutes variantes = même pattern
    ["Rowing barre pronation","Rowing barre supination","Rowing haltère unilatéral","Rowing unilatéral kettlebell","Tirage horizontal élastique"],
    // Abduction machine
    ["Abduction machine assise","Abduction machine"],
    // Donkey kick
    ["Donkey kick quadrupédie","Donkey kicks","Kickback debout"],
    // Soulevé de terre roumain / jambes raides / good morning
    ["Soulevé de terre roumain","Soulevé de terre jambes raides","Good morning"],
  ];
  // Map nom → index de groupe pour lookup O(1)
  const variantGroupIndex = new Map();
  VARIANT_GROUPS.forEach((group, idx) => group.forEach(name => variantGroupIndex.set(name, idx)));

  // ── Noms déjà utilisés dans la séance (déduplication par nom résolu) ──────
  const usedNames = new Set();
  const usedVariantGroups = new Set();

  // ── Helper : slot + mkExCalibrated + reps de base ─────────────────────────
  const se = (pool, slotKey, used, rest, baseReps) => {
    const priorities = SLOTS[slotKey] || [];
    const available = pool.filter(ex => !used.has(ex.id));
    if (available.length === 0) return null;
    const isBlocked = (name) => {
      if (usedNames.has(name)) return true;
      const gIdx = variantGroupIndex.get(name);
      return gIdx !== undefined && usedVariantGroups.has(gIdx);
    };
    // Chercher dans l'ordre de priorité un exercice non bloqué
    let chosen = null;
    for (const name of priorities) {
      const found = available.find(ex => ex.name === name);
      if (found && !isBlocked(found.name)) { chosen = found; break; }
    }
    // Fallback : premier dispo non bloqué
    if (!chosen) chosen = available.find(ex => !isBlocked(ex.name)) || null;
    if (!chosen) return null;
    used.add(chosen.id);
    usedNames.add(chosen.name);
    const gIdx = variantGroupIndex.get(chosen.name);
    if (gIdx !== undefined) usedVariantGroups.add(gIdx);
    return mkExCalibrated(chosen, rest, baseReps);
  };



  // ── Helper : tronquer une séance à nbEx exercices + reset noms pour la prochaine ─
  const trim = (arr) => {
    const result = arr.filter(Boolean).slice(0, nbEx);
    usedNames.clear(); // reset pour la prochaine séance
    usedVariantGroups.clear();
    return result;
  };

  // ── SLOTS (inchangés) ─────────────────────────────────────────────────────
  const SLOTS = {
    pec_horizontal_lourd:["Développé couché barre","Développé couché haltères","Pompes larges","Pompes classiques","Pompes","Développé poitrine élastique"],
    pec_incline:["Développé incliné barre","Développé incliné haltères","Pompes inclinées","Pompes déclinées"],
    pec_horizontal_2:["Écarté haltères plat","Pompes larges","Pompes classiques","Pompes","Développé poitrine élastique"],
    epaule_vertical:["Développé militaire barre","Développé militaire haltères","Développé Arnold","Développé épaules unilatéral kettlebell","Pompes pike"],
    epaule_ant:["Élévations frontales haltères","Développé militaire haltères","Pompes pike","Développé épaules unilatéral kettlebell"],
    epaule_lat:["Élévations latérales haltères","Élévations latérales élastique","Pompes pike"],
    epaule_post:["Oiseau haltères deltoïdes post","Face Pull élastique","Superman latéral"],
    triceps_poly:["Dips triceps barres parallèles","Dips sur chaise","Pompes diamant","Extension nuque haltère","Extension triceps élastique"],
    triceps_iso:["Extension nuque haltère","Extension haltère allongé","Extension triceps élastique","Kickback haltère","Pompes diamant"],
    dos_vertical:["Tractions pronation large","Tractions supination serrée","Tractions neutres","Rowing inversé","Tirage horizontal élastique"],
    dos_horizontal:["Rowing barre pronation","Rowing barre supination","Rowing haltère unilatéral","Rowing unilatéral kettlebell","Tirage horizontal élastique","Rowing inversé"],
    dos_trapezes:["Shrugs haltères","Rowing unilatéral kettlebell","Tirage horizontal élastique","Face Pull élastique","Superman"],
    dos_lombaires:["Soulevé de terre","Soulevé de terre roumain","Good morning","Soulevé de terre kettlebell","Superman"],
    biceps_poly:["Curl barre droite","Curl barre EZ","Curl haltères simultané","Curl biceps élastique","Curl isométrique"],
    biceps_iso:["Curl haltères alterné","Curl marteau","Curl concentration","Curl biceps élastique","Curl isométrique"],
    quad_squat: equipment === "salle"
      ? ["Leg press 45°","Leg press horizontal","Squat barre low bar","Hack squat machine","Squat barre Smith machine","Goblet Squat kettlebell","Squats"]
      : ["Squat barre low bar","Goblet Squat kettlebell","Squat gobelet","Squat avec bande","Squats","Squat sumo","Squat avant front squat"],
    quad_unilateral:["Fentes marchées kettlebell","Fentes avant haltères","Squat bulgare split squat","Fentes arrière reverse lunge","Fentes alternées","Fentes sautées"],
    quad_volume: equipment === "salle"
      ? ["Extension quadriceps machine","Squat sumo","Squat jump","Squats","Fentes latérales"]
      : ["Squat sumo","Squat jump","Squat avant front squat","Squats","Fentes latérales"],
    ischios_hinge: equipment === "salle"
      ? ["Leg curl couché machine","Leg curl allongé","Soulevé de terre roumain","Good morning","Nordic curl GHR"]
      : ["Soulevé de terre roumain","Good morning","Soulevé de terre kettlebell","Nordic curl GHR"],
    ischios_volume: equipment === "salle"
      ? ["Leg curl assis machine","Leg curl assis","Nordic curl GHR","Soulevé de terre jambes raides"]
      : ["Nordic curl GHR","Soulevé de terre jambes raides","Good morning"],
    fessiers_thrust: equipment === "salle"
      ? ["Hip thrust machine","Hip thrust barre","Hip Thrust avec bande","Hip thrust au sol","Pont fessier au sol"]
      : ["Hip thrust barre","Hip Thrust avec bande","Hip thrust au sol","Pont fessier au sol"],
    fessiers_abduction: equipment === "salle"
      ? ["Abduction machine assise","Abduction machine","Monster Walk","Abduction hanche câble","Donkey kicks"]
      : ["Monster Walk","Abduction hanche câble","Clamshell élastique","Hip abduction couchée élastique","Donkey kicks"],
    mollets: equipment === "salle"
      ? ["Mollets debout machine","Mollets assis machine","Mollets debout haltères","Mollets unilatéral escalier"]
      : ["Mollets debout haltères","Mollets unilatéral escalier"],
    gainage_ant:["Planche frontale isométrique","Dead bug","Pallof Press"],
    gainage_dynamique:["Bicycle crunch","Russian Twist kettlebell","Russian twist","Crunch obliques","Gainage latéral"],
    gainage_anti_rotation:["Pallof Press","Gainage latéral","Dead bug","Planche frontale isométrique"],
    abdos_crunch:["Crunch classique","Bicycle crunch","Abdominaux coude genou croisés","Russian twist"],
    // Slots femme — fessiers en 3 sous-familles étanches
    fessiers_grand_lourd: equipment === "salle"
      ? ["Hip thrust machine","Hip thrust barre","Hip thrust au sol","Pont fessier au sol"]
      : ["Hip thrust barre","Hip thrust au sol","Pont fessier au sol"],
    fessiers_grand_unilateral:["Squat bulgare split squat","Fentes arrière reverse lunge","Fentes avant haltères","Fentes avant","Step-up sur chaise"],
    fessiers_grand_isolation:["Donkey kick quadrupédie","Donkey kicks","Kickback debout"],
    fessiers_moyen: equipment === "salle"
      ? ["Abduction machine assise","Abduction machine","Monster Walk","Abduction hanche câble"]
      : ["Monster Walk","Abduction hanche câble","Clamshell élastique","Hip abduction couchée élastique"],
    ischios_isolation: equipment === "salle"
      ? ["Leg curl assis machine","Leg curl couché machine","Nordic curl GHR","Glute ham raise","Soulevé de terre jambes raides"]
      : ["Nordic curl GHR","Glute ham raise","Soulevé de terre jambes raides"],
    core_transverse:["Planche frontale isométrique","Dead bug","Gainage latéral","Planche dynamique","Pallof Press"],
    core_bas_ventre:["Crunch inversé","Relevé de jambes","Scissors (ciseaux)","Dead bug","Planche frontale isométrique"],
    core_dynamique:["Bicycle crunch","Mountain climbers","Planche dynamique","Russian twist","Crunch obliques","Gainage latéral"],
    quad_unilateral_f:["Squat bulgare split squat","Fentes avant haltères","Fentes avant","Fentes arrière reverse lunge","Fentes alternées","Fentes marchées haltères","Step-up sur chaise"],
    ischios_jambes:["Soulevé de terre roumain","Good morning","Soulevé de terre kettlebell"],
    dos_rhomboides:["Face Pull élastique","Oiseau haltères deltoïdes post","Superman","Tirage horizontal élastique","Rowing haltère unilatéral","Rowing buste penché haltères"],
    biceps_leger:["Curl biceps élastique","Curl haltères simultané","Curl marteau","Curl concentration","Curl haltères alterné"],
  };

  // ── Label durée pour le nom du groupe ─────────────────────────────────────
  const durLabel = duration <= 30 ? "30min" : duration <= 45 ? "45min" : duration <= 60 ? "1h" : duration <= 75 ? "1h15" : "1h30";
  const equipLabel = equipment === "salle" ? "Salle" : "Maison";

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── 3 JOURS ──────────────────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════
  if (days === 3) {

    // ── FEMME MAISON 3J ───────────────────────────────────────────────────
    if (gender === "femme" && equipment === "maison") {
      const u1=new Set(),u2=new Set(),u3=new Set();
      const j1 = trim([
        se([...fessiers,...ischios],"fessiers_grand_lourd",u1,180,10),
        se(ischios,"ischios_jambes",u1,120,12),
        se([...fessiers,...quads],"fessiers_grand_unilateral",u1,90,12),
        se(fessiers,"fessiers_moyen",u1,60,15),
        se(fessiers,"fessiers_grand_isolation",u1,60,15),
        se(ischios,"ischios_isolation",u1,75,15),
        se(abdos,"core_transverse",u1,60,30),
        se(abdos,"core_bas_ventre",u1,60,15),
      ]);
      const j2 = trim([
        // Dos
        se(dos,"dos_horizontal",u2,150,10),
        se(dos,"dos_vertical",u2,150,10),
        se(dos,"dos_rhomboides",u2,90,12),
        // Bras
        se(triceps,"triceps_iso",u2,75,15),
        se(biceps,"biceps_leger",u2,75,12),
        // Épaules
        se(epaules,"epaule_lat",u2,60,15),
        se(epaules,"epaule_post",u2,60,15),
        // Abdos
        se(abdos,"core_dynamique",u2,60,20),
      ]);
      const j3 = trim([
        // Quads uniquement
        se(quads,"quad_squat",u3,150,12),
        se(quads,"quad_unilateral_f",u3,120,12),
        se(quads,"quad_volume",u3,90,15),
        // Mollets
        se(mollets,"mollets",u3,60,20),
        // Abdos
        se(abdos,"abdos_crunch",u3,60,20),
        se(abdos,"core_bas_ventre",u3,60,15),
        se(abdos,"core_dynamique",u3,60,20),
        se(abdos,"gainage_ant",u3,60,30),
      ]);
      const gName = `Maison · 3j · ${durLabel}`;
      return [
        {id:101,name:"Jour 1 – Fessiers",    groupName:gName,exercises:j1},
        {id:102,name:"Jour 2 – Haut du corps",groupName:gName,exercises:j2},
        {id:103,name:"Jour 3 – Quadriceps",  groupName:gName,exercises:j3},
      ].filter(p=>p.exercises.length>0);
    }

    // ── FEMME SALLE 3J ────────────────────────────────────────────────────
    if (gender === "femme" && equipment === "salle") {
      const u1=new Set(),u2=new Set(),u3=new Set();
      const j1 = trim([
        // Fessiers hip-dominant
        se(fessiers,"fessiers_grand_lourd",u1,180,10),
        se(fessiers,"fessiers_thrust",u1,150,12),
        se(fessiers,"fessiers_moyen",u1,90,15),
        se(fessiers,"fessiers_grand_isolation",u1,60,15),
        // Ischios
        se(ischios,"ischios_hinge",u1,120,12),
        se(ischios,"ischios_isolation",u1,90,12),
        // Abdos
        se(abdos,"core_transverse",u1,60,30),
        se(abdos,"core_bas_ventre",u1,60,15),
      ]);
      const j2 = trim([
        // Quads uniquement
        se(quads,"quad_squat",u2,180,10),
        se(quads,"quad_unilateral_f",u2,120,12),
        se(quads,"quad_volume",u2,90,15),
        // Mollets
        se(mollets,"mollets",u2,60,20),
        // Abdos
        se(abdos,"abdos_crunch",u2,60,20),
        se(abdos,"core_bas_ventre",u2,60,15),
        se(abdos,"core_dynamique",u2,60,20),
        se(abdos,"gainage_ant",u2,60,30),
      ]);
      const j3 = trim([
        // Dos
        se(dos,"dos_vertical",u3,180,10),
        se(dos,"dos_horizontal",u3,150,10),
        se(dos,"dos_rhomboides",u3,90,12),
        // Bras
        se(biceps,"biceps_poly",u3,75,12),
        se(triceps,"triceps_iso",u3,75,12),
        // Épaules
        se(epaules,"epaule_lat",u3,60,15),
        se(epaules,"epaule_post",u3,60,12),
        // Abdos
        se(abdos,"gainage_ant",u3,60,20),
      ]);
      const gName = `Salle · 3j · ${durLabel}`;
      return [
        {id:101,name:"Jour 1 – Fessiers",    groupName:gName,exercises:j1},
        {id:102,name:"Jour 2 – Quadriceps",  groupName:gName,exercises:j2},
        {id:103,name:"Jour 3 – Haut du corps",groupName:gName,exercises:j3},
      ].filter(p=>p.exercises.length>0);
    }

    // ── HOMME (salle + maison) 3J — PPL ───────────────────────────────────
    const uPush=new Set(),uPull=new Set(),uLegs=new Set();
    const pushExs = trim([
      // Pecs compound d'abord
      se(pecs,"pec_horizontal_lourd",uPush,180,10),
      se(pecs,"pec_incline",uPush,120,10),
      se(pecs,"pec_horizontal_2",uPush,90,12),
      // Triceps
      se(triceps,"triceps_poly",uPush,90,12),
      se(triceps,"triceps_iso",uPush,75,12),
      // Épaules
      se(epaules,"epaule_vertical",uPush,120,10),
      se(epaules,"epaule_lat",uPush,60,15),
      // Abdos en bonus
      se(abdos,"gainage_ant",uPush,60,25),
    ]);
    const pullExs = trim([
      // Dos vertical puis horizontal puis accessoires
      se(dos,"dos_vertical",uPull,180,8),
      se(dos,"dos_horizontal",uPull,150,10),
      se(dos,"dos_trapezes",uPull,90,12),
      se(dos,"dos_lombaires",uPull,60,15),
      // Biceps
      se(biceps,"biceps_poly",uPull,90,12),
      se(biceps,"biceps_iso",uPull,75,12),
      // Épaules post
      se(epaules,"epaule_post",uPull,60,12),
      // Abdos en bonus
      se(abdos,"gainage_dynamique",uPull,60,20),
    ]);
    const legsExs = trim([
      // Quads compound
      se(quads,"quad_squat",uLegs,180,10),
      se(quads,"quad_unilateral",uLegs,120,12),
      se(quads,"quad_volume",uLegs,90,15),
      // Ischios / fessiers
      se(ischios,"ischios_hinge",uLegs,150,10),
      se(fessiers,"fessiers_thrust",uLegs,120,12),
      se(ischios,"ischios_volume",uLegs,90,12),
      // Mollets + abdos
      se(mollets,"mollets",uLegs,60,15),
      se(abdos,"gainage_ant",uLegs,60,40),
    ]);
    const gName3 = `${equipLabel} · 3j · ${durLabel}`;
    return [
      {id:101,name:"Jour 1 - Push",groupName:gName3,exercises:pushExs},
      {id:102,name:"Jour 2 - Pull",groupName:gName3,exercises:pullExs},
      {id:103,name:"Jour 3 - Legs",groupName:gName3,exercises:legsExs},
    ].filter(p=>p.exercises.length>0);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── 4 JOURS ──────────────────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════
  if (days === 4) {

    // ── FEMME MAISON 4J ───────────────────────────────────────────────────
    if (gender === "femme" && equipment === "maison") {
      const u1=new Set(),u2=new Set(),u3=new Set(),u4=new Set();
      const j1 = trim([
        // Fessiers composés lourds
        se([...fessiers,...ischios],"fessiers_grand_lourd",u1,180,10),
        se(ischios,"ischios_jambes",u1,120,12),
        se(quads,"quad_unilateral_f",u1,90,12),
        se(fessiers,"fessiers_grand_isolation",u1,60,15),
        se(fessiers,"fessiers_moyen",u1,60,15),
        se(ischios,"ischios_isolation",u1,75,15),
        se(abdos,"core_transverse",u1,60,30),
        se(abdos,"core_bas_ventre",u1,60,15),
      ]);
      const j2 = trim([
        // Dos
        se(dos,"dos_horizontal",u2,150,10),
        se(dos,"dos_vertical",u2,150,10),
        se(dos,"dos_rhomboides",u2,90,12),
        // Bras
        se(triceps,"triceps_iso",u2,75,15),
        se(biceps,"biceps_leger",u2,75,12),
        // Épaules
        se(epaules,"epaule_lat",u2,60,15),
        se(epaules,"epaule_post",u2,60,15),
        // Abdos
        se(abdos,"core_dynamique",u2,60,20),
      ]);
      const j3 = trim([
        // Quads
        se(quads,"quad_squat",u3,150,12),
        se(quads,"quad_unilateral_f",u3,120,12),
        se(quads,"quad_volume",u3,90,15),
        // Mollets
        se(mollets,"mollets",u3,60,20),
        // Abdos
        se(abdos,"abdos_crunch",u3,60,20),
        se(abdos,"core_bas_ventre",u3,60,15),
        se(abdos,"core_dynamique",u3,60,20),
        se(abdos,"gainage_ant",u3,60,30),
      ]);
      const j4 = trim([
        // Posterior chain — ischios lourds + fessiers abduction/isolation pure
        se(ischios,"ischios_jambes",u4,180,10),
        se(ischios,"ischios_isolation",u4,120,12),
        se(fessiers,"fessiers_grand_unilateral",u4,120,12),
        se(fessiers,"fessiers_abduction",u4,90,15),
        se(fessiers,"fessiers_grand_isolation",u4,60,15),
        se(dos,"dos_rhomboides",u4,60,12),
        se(abdos,"core_transverse",u4,60,30),
        se(abdos,"core_bas_ventre",u4,60,15),
      ]);
      const gName = `Maison · 4j · ${durLabel}`;
      return [
        {id:104,name:"Jour 1 – Fessiers",        groupName:gName,exercises:j1},
        {id:105,name:"Jour 2 – Haut du corps",   groupName:gName,exercises:j2},
        {id:106,name:"Jour 3 – Quadriceps",      groupName:gName,exercises:j3},
        {id:107,name:"Jour 4 – Posterior Chain", groupName:gName,exercises:j4},
      ].filter(p=>p.exercises.length>0);
    }

    // ── FEMME SALLE 4J ────────────────────────────────────────────────────
    if (gender === "femme" && equipment === "salle") {
      const u1=new Set(),u2=new Set(),u3=new Set(),u4=new Set();
      const j1 = trim([
        // Fessiers composés lourds
        se(fessiers,"fessiers_grand_lourd",u1,180,10),
        se(fessiers,"fessiers_thrust",u1,150,12),
        se(ischios,"ischios_hinge",u1,120,12),
        se(fessiers,"fessiers_grand_isolation",u1,60,15),
        se(fessiers,"fessiers_moyen",u1,60,15),
        se(ischios,"ischios_isolation",u1,90,12),
        se(abdos,"core_transverse",u1,60,30),
        se(abdos,"core_bas_ventre",u1,60,15),
      ]);
      const j2 = trim([
        // Quads uniquement
        se(quads,"quad_squat",u2,180,10),
        se(quads,"quad_unilateral_f",u2,120,12),
        se(quads,"quad_volume",u2,90,15),
        // Mollets
        se(mollets,"mollets",u2,60,20),
        // Abdos
        se(abdos,"abdos_crunch",u2,60,20),
        se(abdos,"core_bas_ventre",u2,60,15),
        se(abdos,"core_dynamique",u2,60,20),
        se(abdos,"gainage_ant",u2,60,30),
      ]);
      const j3 = trim([
        // Dos
        se(dos,"dos_vertical",u3,180,10),
        se(dos,"dos_horizontal",u3,150,10),
        se(dos,"dos_rhomboides",u3,90,12),
        // Bras
        se(biceps,"biceps_poly",u3,75,12),
        se(triceps,"triceps_iso",u3,75,12),
        // Épaules
        se(epaules,"epaule_lat",u3,60,15),
        se(epaules,"epaule_post",u3,60,12),
        // Abdos
        se(abdos,"gainage_ant",u3,60,20),
      ]);
      const j4 = trim([
        // Posterior chain : ischios lourds + fessiers abduction/isolation pure
        se(ischios,"ischios_hinge",u4,180,10),
        se(ischios,"ischios_isolation",u4,120,12),
        se(fessiers,"fessiers_grand_unilateral",u4,120,12),
        se(fessiers,"fessiers_abduction",u4,90,15),
        se(fessiers,"fessiers_grand_isolation",u4,60,15),
        se(dos,"dos_lombaires",u4,90,12),
        se(abdos,"core_transverse",u4,60,30),
        se(abdos,"core_bas_ventre",u4,60,15),
      ]);
      const gName = `Salle · 4j · ${durLabel}`;
      return [
        {id:104,name:"Jour 1 – Fessiers",        groupName:gName,exercises:j1},
        {id:105,name:"Jour 2 – Quadriceps",      groupName:gName,exercises:j2},
        {id:106,name:"Jour 3 – Haut du corps",   groupName:gName,exercises:j3},
        {id:107,name:"Jour 4 – Posterior Chain", groupName:gName,exercises:j4},
      ].filter(p=>p.exercises.length>0);
    }

    // ── HOMME (salle + maison) 4J — Upper/Lower ───────────────────────────
    const uUA=new Set(),uLA=new Set(),uUB=new Set(),uLB=new Set();
    const upperAExs = trim([
      // Pecs
      se(pecs,"pec_horizontal_lourd",uUA,180,10),
      se(pecs,"pec_incline",uUA,90,12),
      // Dos
      se(dos,"dos_horizontal",uUA,150,10),
      // Biceps / Triceps
      se(biceps,"biceps_poly",uUA,90,12),
      se(triceps,"triceps_iso",uUA,90,12),
      // Épaules
      se(epaules,"epaule_lat",uUA,60,15),
      se(epaules,"epaule_post",uUA,60,12),
      // Abdos
      se(abdos,"gainage_ant",uUA,60,25),
    ]);
    const lowerAExs = trim([
      // Quads
      se(quads,"quad_squat",uLA,180,10),
      se(quads,"quad_unilateral",uLA,120,12),
      se(quads,"quad_volume",uLA,90,15),
      // Ischios / Fessiers
      se(ischios,"ischios_hinge",uLA,150,12),
      se(fessiers,"fessiers_thrust",uLA,120,12),
      se(ischios,"ischios_volume",uLA,90,12),
      // Mollets + Abdos
      se(mollets,"mollets",uLA,60,15),
      se(abdos,"gainage_dynamique",uLA,60,35),
    ]);
    const upperBExs = trim([
      // Dos
      se(dos,"dos_vertical",uUB,180,8),
      se(dos,"dos_horizontal",uUB,150,10),
      // Pecs incliné
      se(pecs,"pec_incline",uUB,120,10),
      // Biceps / Triceps
      se(biceps,"biceps_iso",uUB,90,12),
      se(triceps,"triceps_poly",uUB,75,12),
      // Épaules
      se(epaules,"epaule_vertical",uUB,90,10),
      se(epaules,"epaule_post",uUB,60,12),
      // Abdos
      se(abdos,"gainage_anti_rotation",uUB,60,15),
    ]);
    const lowerBExs = trim([
      // Ischios / Fessiers
      se(ischios,"ischios_hinge",uLB,180,10),
      se(fessiers,"fessiers_thrust",uLB,150,12),
      se(fessiers,"fessiers_abduction",uLB,60,15),
      // Quads
      se(quads,"quad_squat",uLB,120,12),
      se(quads,"quad_unilateral",uLB,90,12),
      // Mollets + Abdos
      se(mollets,"mollets",uLB,60,15),
      se(abdos,"gainage_anti_rotation",uLB,60,12),
      se(abdos,"gainage_dynamique",uLB,60,25),
    ]);
    const gName4 = `${equipLabel} · 4j · ${durLabel}`;
    return [
      {id:104,name:"Jour 1 - Upper A",groupName:gName4,exercises:upperAExs},
      {id:105,name:"Jour 2 - Lower A",groupName:gName4,exercises:lowerAExs},
      {id:106,name:"Jour 3 - Upper B",groupName:gName4,exercises:upperBExs},
      {id:107,name:"Jour 4 - Lower B",groupName:gName4,exercises:lowerBExs},
    ].filter(p=>p.exercises.length>0);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ─── 5+ JOURS ─────────────────────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════════════
  if (days >= 5) {

    // ── FEMME MAISON 5J ───────────────────────────────────────────────────
    if (gender === "femme" && equipment === "maison") {
      const u1=new Set(),u2=new Set(),u3=new Set(),u4=new Set(),u5=new Set();
      const j1 = trim([
        // Fessiers Force — hip-dominant lourd
        se([...fessiers,...ischios],"fessiers_grand_lourd",u1,180,10),
        se(fessiers,"fessiers_thrust",u1,150,12),
        se(ischios,"ischios_jambes",u1,120,12),
        se(fessiers,"fessiers_grand_isolation",u1,60,15),
        se(fessiers,"fessiers_moyen",u1,60,15),
        se(ischios,"ischios_isolation",u1,75,15),
        se(abdos,"core_transverse",u1,60,30),
        se(abdos,"core_bas_ventre",u1,60,15),
      ]);
      const j2 = trim([
        // Quads Force
        se(quads,"quad_squat",u2,180,10),
        se(quads,"quad_unilateral_f",u2,120,12),
        se(quads,"quad_volume",u2,90,15),
        se(mollets,"mollets",u2,60,20),
        se(abdos,"abdos_crunch",u2,60,20),
        se(abdos,"core_bas_ventre",u2,60,15),
        se(abdos,"core_dynamique",u2,60,20),
        se(abdos,"gainage_ant",u2,60,30),
      ]);
      const j3 = trim([
        // Upper
        se(dos,"dos_horizontal",u3,150,10),
        se(dos,"dos_vertical",u3,150,10),
        se(dos,"dos_rhomboides",u3,90,12),
        se(triceps,"triceps_iso",u3,75,15),
        se(biceps,"biceps_leger",u3,75,12),
        se(epaules,"epaule_lat",u3,60,15),
        se(epaules,"epaule_post",u3,60,15),
        se(abdos,"core_transverse",u3,60,30),
      ]);
      const j4 = trim([
        // Fessiers Pump — abduction + isolation, sans hip thrust
        se(fessiers,"fessiers_grand_unilateral",u4,150,12),
        se(ischios,"ischios_isolation",u4,120,12),
        se(fessiers,"fessiers_abduction",u4,90,20),
        se(fessiers,"fessiers_moyen",u4,90,20),
        se(fessiers,"fessiers_grand_isolation",u4,75,15),
        se(ischios,"ischios_jambes",u4,90,12),
        se(abdos,"core_transverse",u4,60,30),
        se(abdos,"core_bas_ventre",u4,60,15),
      ]);
      const j5 = trim([
        // Quads Volume
        se(quads,"quad_unilateral_f",u5,120,12),
        se(quads,"quad_volume",u5,90,15),
        se(quads,"quad_squat",u5,90,12),
        se(mollets,"mollets",u5,60,20),
        se(abdos,"abdos_crunch",u5,60,20),
        se(abdos,"core_bas_ventre",u5,60,15),
        se(abdos,"core_dynamique",u5,60,20),
        se(abdos,"gainage_ant",u5,60,30),
      ]);
      const gName = `Maison · 5j · ${durLabel}`;
      return [
        {id:201,name:"Jour 1 – Fessiers Force",  groupName:gName,exercises:j1},
        {id:202,name:"Jour 2 – Quadriceps",      groupName:gName,exercises:j2},
        {id:203,name:"Jour 3 – Haut du corps",   groupName:gName,exercises:j3},
        {id:204,name:"Jour 4 – Fessiers Pump",   groupName:gName,exercises:j4},
        {id:205,name:"Jour 5 – Quadriceps Vol.", groupName:gName,exercises:j5},
      ].filter(p=>p.exercises.length>0);
    }

    // ── FEMME SALLE 5J ────────────────────────────────────────────────────
    if (gender === "femme" && equipment === "salle") {
      const u1=new Set(),u2=new Set(),u3=new Set(),u4=new Set(),u5=new Set();
      const j1 = trim([
        // Fessiers hip-dominant
        se(fessiers,"fessiers_grand_lourd",u1,180,10),
        se(fessiers,"fessiers_thrust",u1,150,12),
        se(ischios,"ischios_hinge",u1,120,12),
        se(fessiers,"fessiers_grand_isolation",u1,60,15),
        se(fessiers,"fessiers_moyen",u1,60,15),
        se(ischios,"ischios_isolation",u1,90,12),
        // Abdos
        se(abdos,"core_transverse",u1,60,30),
        se(abdos,"core_bas_ventre",u1,60,15),
      ]);
      const j2 = trim([
        // Quads Force
        se(quads,"quad_squat",u2,180,10),
        se(quads,"quad_unilateral_f",u2,120,12),
        se(quads,"quad_volume",u2,90,15),
        se(mollets,"mollets",u2,60,20),
        se(abdos,"abdos_crunch",u2,60,20),
        se(abdos,"core_bas_ventre",u2,60,15),
        se(abdos,"core_dynamique",u2,60,20),
        se(abdos,"gainage_ant",u2,60,30),
      ]);
      const j3 = trim([
        // Upper
        se(dos,"dos_vertical",u3,180,10),
        se(dos,"dos_horizontal",u3,150,10),
        se(dos,"dos_rhomboides",u3,90,12),
        se(biceps,"biceps_poly",u3,75,12),
        se(triceps,"triceps_iso",u3,75,12),
        se(epaules,"epaule_lat",u3,60,15),
        se(epaules,"epaule_post",u3,60,12),
        se(abdos,"gainage_ant",u3,60,20),
      ]);
      const j4 = trim([
        // Fessiers Pump — abduction + isolation, sans hip thrust
        se(fessiers,"fessiers_grand_unilateral",u4,150,12),
        se(ischios,"ischios_isolation",u4,120,12),
        se(fessiers,"fessiers_abduction",u4,90,20),
        se(fessiers,"fessiers_moyen",u4,90,20),
        se(fessiers,"fessiers_grand_isolation",u4,75,15),
        se(dos,"dos_lombaires",u4,90,12),
        se(abdos,"core_transverse",u4,60,30),
        se(abdos,"core_bas_ventre",u4,60,15),
      ]);
      const j5 = trim([
        // Quads Volume
        se(quads,"quad_unilateral_f",u5,120,12),
        se(quads,"quad_volume",u5,90,15),
        se(quads,"quad_squat",u5,90,12),
        se(mollets,"mollets",u5,60,20),
        se(abdos,"abdos_crunch",u5,60,20),
        se(abdos,"core_bas_ventre",u5,60,15),
        se(abdos,"core_dynamique",u5,60,20),
        se(abdos,"gainage_ant",u5,60,30),
      ]);
      const gName = `Salle · 5j · ${durLabel}`;
      return [
        {id:201,name:"Jour 1 – Fessiers Force",  groupName:gName,exercises:j1},
        {id:202,name:"Jour 2 – Quadriceps",      groupName:gName,exercises:j2},
        {id:203,name:"Jour 3 – Haut du corps",   groupName:gName,exercises:j3},
        {id:204,name:"Jour 4 – Fessiers Pump",   groupName:gName,exercises:j4},
        {id:205,name:"Jour 5 – Quadriceps Vol.", groupName:gName,exercises:j5},
      ].filter(p=>p.exercises.length>0);
    }

    // ── HOMME (salle + maison) 5J — PPL + Upper/Lower ─────────────────────
    const uP=new Set(),uPu=new Set(),uL=new Set(),uU=new Set(),uLv=new Set();
    const pushExs = trim([
      // Pecs
      se(pecs,"pec_horizontal_lourd",uP,180,10),
      se(pecs,"pec_incline",uP,90,12),
      // Triceps
      se(triceps,"triceps_poly",uP,120,12),
      se(triceps,"triceps_iso",uP,90,12),
      // Épaules
      se(epaules,"epaule_vertical",uP,150,10),
      se(epaules,"epaule_lat",uP,60,15),
      se(epaules,"epaule_ant",uP,60,12),
      // Abdos
      se(abdos,"gainage_ant",uP,60,25),
    ]);
    const pullExs = trim([
      // Dos vertical puis horizontal
      se(dos,"dos_vertical",uPu,180,8),
      se(dos,"dos_horizontal",uPu,150,10),
      se(dos,"dos_trapezes",uPu,90,12),
      se(dos,"dos_lombaires",uPu,60,15),
      // Biceps
      se(biceps,"biceps_poly",uPu,90,12),
      se(biceps,"biceps_iso",uPu,75,12),
      // Épaules post
      se(epaules,"epaule_post",uPu,60,12),
      // Abdos
      se(abdos,"gainage_dynamique",uPu,60,20),
    ]);
    const legsExs = trim([
      // Quads
      se(quads,"quad_squat",uL,180,10),
      se(quads,"quad_unilateral",uL,120,12),
      se(quads,"quad_volume",uL,90,15),
      // Ischios / Fessiers
      se(ischios,"ischios_hinge",uL,150,10),
      se(fessiers,"fessiers_thrust",uL,120,12),
      se(ischios,"ischios_volume",uL,90,12),
      // Mollets + Abdos
      se(mollets,"mollets",uL,60,15),
      se(abdos,"gainage_ant",uL,60,40),
    ]);
    const upperExs = trim([
      // Pecs
      se(pecs,"pec_incline",uU,120,10),
      se(pecs,"pec_horizontal_2",uU,90,12),
      // Dos
      se(dos,"dos_horizontal",uU,90,10),
      // Biceps / Triceps
      se(biceps,"biceps_iso",uU,90,12),
      se(triceps,"triceps_iso",uU,90,12),
      // Épaules
      se(epaules,"epaule_lat",uU,60,15),
      se(epaules,"epaule_post",uU,60,12),
      // Abdos
      se(abdos,"gainage_ant",uU,60,25),
    ]);
    const lowerVExs = trim([
      // Quads
      se(quads,"quad_squat",uLv,150,12),
      se(quads,"quad_unilateral",uLv,90,12),
      // Fessiers / Ischios
      se(fessiers,"fessiers_thrust",uLv,120,12),
      se(ischios,"ischios_volume",uLv,90,12),
      se(fessiers,"fessiers_abduction",uLv,60,15),
      // Mollets + Abdos
      se(mollets,"mollets",uLv,60,15),
      se(abdos,"abdos_crunch",uLv,60,20),
      se([...abdos,...quads],"gainage_dynamique",uLv,60,30),
    ]);
    const gName5 = `${equipLabel} · 5j · ${durLabel}`;
    return [
      {id:201,name:"Jour 1 - Push Force",  groupName:gName5,exercises:pushExs},
      {id:202,name:"Jour 2 - Pull Force",  groupName:gName5,exercises:pullExs},
      {id:203,name:"Jour 3 - Legs Force",  groupName:gName5,exercises:legsExs},
      {id:204,name:"Jour 4 - Upper Vol.",  groupName:gName5,exercises:upperExs},
      {id:205,name:"Jour 5 - Lower Vol.",  groupName:gName5,exercises:lowerVExs},
    ].filter(p=>p.exercises.length>0);
  }

  return [];
}
